import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useCallback, useEffect } from "react";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export interface PagedResult<T> {
  data: T[];
  total: number; // total items count
}

export interface UsePatientActivityParams<T> {
  patientId?: string;
  /** Unique key to isolate caches across different pages/metrics */
  queryKey?: string;
  /** Initial page size, default 10 */
  initialPageSize?: number;
  /** Provide a page fetcher that returns merged rows and total count */
  fetchPage: (page: number, pageSize: number) => Promise<PagedResult<T>>;
}

export interface UsePatientActivityResult<T> {
  data: T[];
  total: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  isLoading: boolean;
  error: string | null;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  refetch: () => void;
}

export function usePatientActivity<T>({
  patientId,
  queryKey = "activity",
  initialPageSize = 10,
  fetchPage,
}: UsePatientActivityParams<T>): UsePatientActivityResult<T> {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);

  useEffect(() => {
    setPage(0);
  }, [patientId]);

  const {
    data: result,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [queryKey, patientId, page, rowsPerPage],
    queryFn: async () => {
      if (!patientId) return { data: [], total: 0 } as PagedResult<T>;
      return (await fetchPage(page, rowsPerPage)) as PagedResult<T>;
    },
    enabled: !!patientId,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const total = result?.total ?? 0;
  const data = result?.data ?? [];

  const rowsPerPageOptions = useMemo(() => {
    if (total === 0) return [DEFAULT_ROWS_PER_PAGE_OPTIONS[0]];
    const firstMatch =
      DEFAULT_ROWS_PER_PAGE_OPTIONS.find((item) => item >= total) ??
      DEFAULT_ROWS_PER_PAGE_OPTIONS[0];
    return DEFAULT_ROWS_PER_PAGE_OPTIONS.filter((item) => item <= firstMatch);
  }, [total]);

  const onPageChange = useCallback((_: unknown, newPage: number) => {
    if (newPage < 0) {
      setPage(0);
    } else {
      setPage(newPage);
    }
  }, []);

  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Unknown error"
    : null;

  return {
    data,
    total,
    page,
    rowsPerPage,
    rowsPerPageOptions,
    isLoading,
    error,
    onPageChange,
    onRowsPerPageChange,
    refetch,
  };
}

export default usePatientActivity;
